import {FC, useEffect, useRef, useState} from 'react'
import {PromoBanner, PromoBannerFormValues, PromoBannerStyle} from '../types'
import {ConfirmDialog} from './ConfirmDialog'

interface Props {
  slotLabel: string
  initial: PromoBanner | null
  saving: boolean
  onSave: (values: PromoBannerFormValues, id?: number) => Promise<void>
  onDelete?: (id: number) => Promise<void>
}

const DEFAULT_VALUES: PromoBannerFormValues = {
  image: null,
  link: '',
  alt_text: '',
  style: 'bordered',
  position: 0,
  is_active: true,
}

export const PromoBannerCard: FC<Props> = ({slotLabel, initial, saving, onSave, onDelete}) => {
  const [values, setValues] = useState<PromoBannerFormValues>(DEFAULT_VALUES)
  const [preview, setPreview] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)
  // Inline validation errors used to render as window.alert(). Now they
  // surface through the same Nobilis-styled dialog as Delete, but as a
  // notice (single Accept button).
  const [notice, setNotice] = useState<{title: string; description: string} | null>(null)

  const handleConfirmDelete = async () => {
    if (!initial || !onDelete) return
    setDeleting(true)
    try {
      await onDelete(initial.id)
    } finally {
      setDeleting(false)
      setConfirmDelete(false)
    }
  }

  useEffect(() => {
    if (initial) {
      setValues({
        id: initial.id,
        image: initial.image,
        link: initial.link,
        alt_text: initial.alt_text,
        style: initial.style,
        position: initial.position,
        is_active: initial.is_active,
      })
      setPreview(initial.image || null)
    } else {
      setValues(DEFAULT_VALUES)
      setPreview(null)
    }
  }, [initial])

  useEffect(() => {
    if (values.image instanceof File) {
      const url = URL.createObjectURL(values.image)
      setPreview(url)
      return () => URL.revokeObjectURL(url)
    }
    if (typeof values.image === 'string' && values.image) {
      setPreview(values.image)
    }
  }, [values.image])

  const handleFile = (file: File | null) => {
    if (!file) return
    const allowed = ['image/jpeg', 'image/png', 'image/webp']
    if (!allowed.includes(file.type)) {
      setNotice({
        title: 'Unsupported file type',
        description: 'Only JPG, PNG or WEBP images are allowed.',
      })
      return
    }
    setValues((v) => ({...v, image: file}))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!values.image) {
      setNotice({
        title: 'Image required',
        description: 'Please upload an image before saving the banner.',
      })
      return
    }
    if (!values.link) {
      setNotice({
        title: 'Destination link required',
        description: 'Please enter a destination link before saving the banner.',
      })
      return
    }
    await onSave(values, values.id)
  }

  const dimensionsLabel =
    values.style === 'borderless' ? '1514 × 653 (borderless)' : '1280 × 653 (bordered)'

  return (
    <form
      onSubmit={handleSubmit}
      className='card mb-4'
      style={{background: '#ffffff', border: '1px solid #e9e9e9', borderRadius: 0}}
    >
      <div
        className='card-header d-flex align-items-center justify-content-between'
        style={{padding: '16px 24px', borderBottom: '1px solid #e9e9e9'}}
      >
        <div>
          <span
            style={{
              fontFamily: 'Oswald, sans-serif',
              fontWeight: 600,
              fontSize: 15,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: '#151515',
            }}
          >
            {slotLabel}
          </span>
          <span style={{color: '#6b6b6b', fontSize: 12, marginLeft: 12}}>{dimensionsLabel}</span>
        </div>
        {initial && onDelete && (
          <button
            type='button'
            onClick={() => setConfirmDelete(true)}
            disabled={saving}
            style={{
              border: 'none',
              background: 'transparent',
              color: '#a02020',
              textTransform: 'uppercase',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.1em',
              cursor: 'pointer',
              padding: '6px 10px',
            }}
          >
            Delete
          </button>
        )}
      </div>

      <div
        className='card-body'
        style={{padding: 24, display: 'grid', gridTemplateColumns: '260px 1fr', gap: 24}}
      >
        <div>
          <div
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault()
              handleFile(e.dataTransfer.files?.[0] ?? null)
            }}
            style={{
              aspectRatio: '1514 / 653',
              background: preview ? `url(${preview}) center/cover no-repeat` : '#efe9dd',
              border: preview ? '1px solid #e9e9e9' : '2px dashed #cfc9be',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              textAlign: 'center',
              padding: 12,
            }}
          >
            {!preview && (
              <div>
                <div
                  style={{
                    fontFamily: "'Cormorant SC', serif",
                    fontSize: 20,
                    color: '#151515',
                    marginBottom: 4,
                  }}
                >
                  Image
                </div>
                <div style={{color: '#6b6b6b', fontSize: 11}}>
                  Drag or click to select
                  <br />
                  JPG / PNG / WEBP
                </div>
              </div>
            )}
          </div>
          <input
            type='file'
            ref={inputRef}
            accept='.jpg,.jpeg,.png,.webp'
            style={{display: 'none'}}
            onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
          />
        </div>

        <div className='d-flex flex-column' style={{gap: 14}}>
          <div>
            <label className='form-label' style={labelStyle}>
              Destination link
            </label>
            <input
              type='url'
              className='form-control'
              value={values.link}
              onChange={(e) => setValues((v) => ({...v, link: e.target.value}))}
              placeholder='https://nobilis.com/...'
              style={inputStyle}
            />
          </div>
          <div>
            <label className='form-label' style={labelStyle}>
              Alt text (accessibility)
            </label>
            <input
              type='text'
              className='form-control'
              value={values.alt_text}
              onChange={(e) => setValues((v) => ({...v, alt_text: e.target.value}))}
              placeholder='Short description'
              style={inputStyle}
            />
          </div>
          <div className='d-flex' style={{gap: 20, alignItems: 'center'}}>
            <div>
              <label className='form-label' style={labelStyle}>
                Style
              </label>
              <select
                className='form-select'
                value={values.style}
                onChange={(e) =>
                  setValues((v) => ({...v, style: e.target.value as PromoBannerStyle}))
                }
                style={inputStyle}
              >
                <option value='bordered'>Bordered (1280 × 653)</option>
                <option value='borderless'>Borderless (1514 × 653)</option>
              </select>
            </div>
            <div>
              <label className='form-label' style={labelStyle}>
                Position
              </label>
              <input
                type='number'
                className='form-control'
                value={values.position}
                onChange={(e) =>
                  setValues((v) => ({...v, position: parseInt(e.target.value, 10) || 0}))
                }
                style={{...inputStyle, width: 90}}
              />
            </div>
            <label
              className='d-inline-flex align-items-center'
              style={{gap: 8, cursor: 'pointer', fontSize: 13, marginTop: 24}}
            >
              <input
                type='checkbox'
                checked={values.is_active}
                onChange={(e) => setValues((v) => ({...v, is_active: e.target.checked}))}
                style={{accentColor: '#460107'}}
              />
              <span>Active</span>
            </label>
          </div>
          <div style={{marginTop: 8}}>
            <button
              type='submit'
              disabled={saving}
              style={{
                padding: '10px 20px',
                border: '1px solid #460107',
                background: '#460107',
                color: '#ffffff',
                textTransform: 'uppercase',
                letterSpacing: '0.12em',
                fontSize: 11,
                fontWeight: 700,
                cursor: saving ? 'not-allowed' : 'pointer',
              }}
            >
              {saving ? 'Saving...' : initial ? 'Save changes' : 'Create banner'}
            </button>
          </div>
        </div>
      </div>
      {initial && onDelete && (
        <ConfirmDialog
          show={confirmDelete}
          title='Delete banner'
          description='This banner will be removed from the dashboard. Members will no longer see it. This action cannot be undone.'
          cancelLabel='Cancel'
          acceptLabel='Accept'
          loading={deleting}
          onCancel={() => (deleting ? undefined : setConfirmDelete(false))}
          onAccept={handleConfirmDelete}
        />
      )}
      <ConfirmDialog
        show={notice !== null}
        variant='notice'
        title={notice?.title ?? ''}
        description={notice?.description}
        acceptLabel='OK'
        onAccept={() => setNotice(null)}
      />
    </form>
  )
}

const labelStyle: React.CSSProperties = {
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
  color: '#6b6b6b',
  fontWeight: 700,
  display: 'block',
  marginBottom: 6,
}

const inputStyle: React.CSSProperties = {
  border: '1px solid #e9e9e9',
  background: '#ffffff',
  color: '#151515',
  padding: '10px 12px',
  fontSize: 14,
  borderRadius: 0,
  width: '100%',
}
